import { getConfigValue } from "@rumble-it/common-lib/src/config";
import axios, { type AxiosError } from "axios";
import { MIME_TYPE_JSON_LD } from "@rumble-it/common-lib/src/constants/mimeTypes";
import { User } from "oidc-client-ts";

const axiosInstance = axios.create({
  headers: {
    Accept: MIME_TYPE_JSON_LD,
    "Content-Type": MIME_TYPE_JSON_LD,
  },
});

axiosInstance.interceptors.request.use(async (config) => {
  const oidcStorage = sessionStorage.getItem(
    `oidc.user:${getConfigValue("authUrl")}/realms/${getConfigValue("authRealm")}:${getConfigValue("authClientId")}`,
  );

  if (!oidcStorage) {
    throw new Error("OIDC user not found in session storage.");
  }

  const user = User.fromStorageString(oidcStorage);

  const affiliateStorage = sessionStorage.getItem(
    `api.user.affiliate:${user?.profile?.sub}`,
  );

  if (!affiliateStorage) {
    throw new Error("Affiliate ID not found in session storage.");
  }

  const affiliateId = affiliateStorage;

  const accessToken = user?.access_token || null;

  if (accessToken === null) {
    throw new Error("Access token is missing. Please log in.");
  }

  if (affiliateId === null) {
    throw new Error("Failed to get affiliate ID.");
  }

  config.headers.Authorization = `Bearer ${accessToken}`;

  if (config.isRootRequest) {
    config.baseURL = `${getConfigValue("apiUrl")}/root`;
  } else {
    config.baseURL = `${getConfigValue("apiUrl")}/affiliates/${affiliateId}`;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    const supportedContexts = [
      "/contexts/Dossier",
      "/contexts/Affiliate",
      "/contexts/AffiliateResource",
      "/contexts/ServicePartner",
      "/contexts/ServicePartnerResource",
      "/contexts/ServicePartnerCollectionResource",
      "/contexts/ServicePartnerTagResource",
      "/contexts/SmsTemplate",
      "/contexts/User",
      "/contexts/ClaimResource",
      "/contexts/RecourseResource",
      "/contexts/TaskResource",
      "/contexts/SmsTemplate",
      "/contexts/Rule",
      "/contexts/Company",
      "/contexts/WorkflowStates",
      "/contexts/AppointmentTimeSlotPlan",
      "/contexts/DossierType",
      "/contexts/BrandResource",
      "/contexts/ReplacementTransportOptionResource",
      "/contexts/ReplacementTransportPlanResource",
      "/contexts/ConceptResource",
      "/contexts/DossierConceptResource",
      "/contexts/DossierTypeResource",
      "/contexts/DocumentLibraryResource",
      "/contexts/AgentSession",
      "/contexts/DocumentTemplateResource",
      "/contexts/ExternalWebhookResource",
      "/contexts/FaqResource",
      "/contexts/FormConfigurationResource",
      "/contexts/WorkflowStatesResource",
      "/contexts/QueryExpression",
      "/contexts/Agent",
      "/contexts/RuleAction",
      "/contexts/BenchmarkRun",
      "/contexts/BenchmarkTestCase",
      "/contexts/BenchmarkResult",
      "/contexts/ChatConversationResource",
    ];
    // During admin refactor, support both Collection and plain arrays
    if (supportedContexts.includes(response.data["@context"])) {
      return response;
    } else if (response.data["@type"] === "Collection") {
      response.data = response.data["member"] || [];
    }

    return response;
  },
  async (error: AxiosError) => {
    //TODO: Handle Unauthorized errors and refetching the session
    console.error("Request failed:", error);
    return Promise.reject(error);
  },
);

export { axiosInstance };
